// Compact tile grid that replaces the inline secondary sections in
// the post-10pm compositions (results_pending + counting). Each tile
// is a link to an anchor on /explore (for in-page sections) or a
// direct URL (for /turnout + external partners).
//
// Visual treatment: solid brand-red card with a single white line
// icon centred top-half and the section label below. Uniform across
// every tile because each icon is hand-drawn in the same stroke
// weight + viewBox — no per-OS emoji rendering variance, no dependent
// art assets to source / sync.

import './SectionTiles.css';

type IconKey =
  | 'survey'
  | 'quiz'
  | 'map'
  | 'candidates'
  | 'proposals'
  | 'manifestos'
  | 'turnout'
  | 'spunt'
  | 'votmt';

interface Tile {
  label: string;
  kicker: string;
  /** Where the tile links. If `anchor` starts with `#`, the tile
   *  links to /explore<anchor>. Otherwise treat it as a full URL or
   *  app path. `external: true` opens in a new tab + adds rel=noopener. */
  anchor: string;
  external?: boolean;
  icon: IconKey;
}

const TILES: Tile[] = [
  { label: 'Survey Tracker', kicker: 'The polls',          anchor: '#surveys',     icon: 'survey' },
  { label: 'Quiz',           kicker: 'Find your match',    anchor: '#quiz',        icon: 'quiz' },
  { label: 'District Map',   kicker: '2022 results',       anchor: '#map',         icon: 'map' },
  { label: 'Candidates',     kicker: 'The 2026 field',     anchor: '#candidates',  icon: 'candidates' },
  { label: 'Proposals',      kicker: 'PL vs PN',           anchor: '#proposals',   icon: 'proposals' },
  { label: 'Manifestos',     kicker: 'Ask the AI',         anchor: '#manifestos',  icon: 'manifestos' },
  // Counting-phase additions — Turnout/Spunt/Vot.mt got condensed
  // out of their own full-width sections into the same tile row so
  // the centre of the page stays focused on the live stream + map +
  // who's-elected band above.
  { label: 'Turnout',        kicker: 'Live by district',   anchor: '/turnout',                          icon: 'turnout' },
  { label: 'Spunt',          kicker: 'Fantasy football',   anchor: 'https://spunt.mt', external: true,  icon: 'spunt' },
  { label: 'vot.mt',         kicker: 'Where do I vote?',   anchor: 'https://vot.mt',   external: true,  icon: 'votmt' },
];

function tileHref(t: Tile): string {
  if (t.external) return t.anchor;
  if (t.anchor.startsWith('#')) return `/explore${t.anchor}`;
  return t.anchor;
}

export default function SectionTiles() {
  return (
    <section className="section-tiles" aria-label="Explore the dashboard">
      <header className="section-tiles__head">
        <p className="section-tiles__kicker">Also on the dashboard</p>
        <h2 className="section-tiles__title">Explore</h2>
      </header>
      <ul className="section-tiles__grid">
        {TILES.map((t) => (
          <li key={t.anchor} className="section-tiles__cell">
            <a
              className="section-tiles__tile"
              href={tileHref(t)}
              target={t.external ? '_blank' : undefined}
              rel={t.external ? 'noopener noreferrer' : undefined}
              aria-label={`${t.label} — ${t.kicker}`}
            >
              <span className="section-tiles__tile-icon" aria-hidden="true">
                <TileIcon name={t.icon} />
              </span>
              <span className="section-tiles__tile-body">
                <span className="section-tiles__tile-kicker">{t.kicker}</span>
                <span className="section-tiles__tile-label">{t.label}</span>
              </span>
              <span className="section-tiles__tile-arrow" aria-hidden="true">
                {t.external ? '↗' : '→'}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── Icons ──────────────────────────────────────────────────────────
//
// One JSX per IconKey. All drawn in a 24×24 viewBox with stroke-width
// 1.75, rounded caps + joins, no fill — same visual language across
// every tile. Colour comes from `currentColor` so CSS controls it
// (white on the red brand bg). Add a new icon: extend IconKey, add a
// case here, point a Tile.icon at it.

function TileIcon({ name }: { name: IconKey }) {
  const common = {
    width: '100%',
    height: '100%',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (name) {
    case 'survey':
      // Bar-chart trio — three vertical bars of increasing height.
      return (
        <svg {...common}>
          <line x1="5" y1="20" x2="5" y2="14" />
          <line x1="12" y1="20" x2="12" y2="10" />
          <line x1="19" y1="20" x2="19" y2="6" />
          <line x1="3" y1="20" x2="21" y2="20" />
        </svg>
      );
    case 'quiz':
      // Question mark in a circle.
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.5 9.5a2.5 2.5 0 1 1 4.5 1.5c-.7.7-2 1.2-2 2.5" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case 'map':
      // Three-fold map with a pin.
      return (
        <svg {...common}>
          <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z" />
          <line x1="9" y1="4" x2="9" y2="20" />
          <line x1="15" y1="6" x2="15" y2="22" />
        </svg>
      );
    case 'candidates':
      // Group of three people silhouettes.
      return (
        <svg {...common}>
          <circle cx="9" cy="9" r="3" />
          <path d="M3 19a6 6 0 0 1 12 0" />
          <circle cx="17" cy="8" r="2.5" />
          <path d="M15 19a5 5 0 0 1 6-4.5" />
        </svg>
      );
    case 'proposals':
      // Document with bullet lines.
      return (
        <svg {...common}>
          <path d="M6 3h9l4 4v14H6z" />
          <path d="M15 3v4h4" />
          <line x1="9" y1="13" x2="16" y2="13" />
          <line x1="9" y1="17" x2="14" y2="17" />
          <line x1="9" y1="9" x2="12" y2="9" />
        </svg>
      );
    case 'manifestos':
      // Sparkles — AI/answer/idea cue.
      return (
        <svg {...common}>
          <path d="M12 3l1.8 4.2L18 9l-4.2 1.8L12 15l-1.8-4.2L6 9l4.2-1.8z" />
          <path d="M18 15l.8 1.7 1.7.8-1.7.8L18 20l-.8-1.7-1.7-.8 1.7-.8z" />
          <path d="M5 17l.6 1.3 1.3.6-1.3.6L5 20.8l-.6-1.3-1.3-.6 1.3-.6z" />
        </svg>
      );
    case 'turnout':
      // Ballot dropping into a box.
      return (
        <svg {...common}>
          <rect x="4" y="11" width="16" height="10" rx="1.5" />
          <path d="M9 11V5h6v6" />
          <line x1="10" y1="16" x2="14" y2="16" />
        </svg>
      );
    case 'spunt':
      // Football / soccer ball — hex pattern.
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 3l4 3 -1.5 5h-5L8 6z" />
          <path d="M5 11l3 -1.5" />
          <path d="M19 11l-3 -1.5" />
          <path d="M9.5 16l-1.5 4" />
          <path d="M14.5 16l1.5 4" />
        </svg>
      );
    case 'votmt':
      // Location pin.
      return (
        <svg {...common}>
          <path d="M12 21s-7-7.5-7-12a7 7 0 1 1 14 0c0 4.5-7 12-7 12z" />
          <circle cx="12" cy="9" r="2.5" />
        </svg>
      );
  }
}
